import {
  columnFacetingFeature,
  columnFilteringFeature,
  columnOrderingFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_arrIncludes,
  filterFn_equals,
  filterFn_inDateRange,
  filterFn_inNumberRange,
  filterFn_includesString,
  filterFn_weakEquals,
  globalFilteringFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  type Cell as TanStackCell,
  type CellContext as TanStackCellContext,
  type CellData,
  type Column as TanStackColumn,
  type ColumnDef as TanStackColumnDef,
  type Header as TanStackHeader,
  type ReactTable as TanStackReactTable,
  type Row as TanStackRow,
  type RowData,
  type Table as TanStackTable,
  type TableOptions as TanStackTableOptions,
  type ColumnVisibilityState,
} from '@tanstack/react-table';

/**
 * Column alignment carried on `columnDef.meta`. `{ align: 'right' }` marks a
 * numeric column: `DataTable` right-aligns its header and cells with tabular
 * numerals so the figures line up, and `DataTableColumnHeader` follows suit.
 * Read through `columnAlign` because other tables keep their own keys in
 * `meta`, so `ColumnMeta` is deliberately left untyped app-wide.
 */
export type ColumnAlign = 'left' | 'right';

export function columnAlign(meta: unknown): ColumnAlign {
  return typeof meta === 'object' &&
    meta !== null &&
    'align' in meta &&
    meta.align === 'right'
    ? 'right'
    : 'left';
}

/** Header/cell classes for a column's `meta.align`. */
export function columnAlignClass(meta: unknown): string | undefined {
  return columnAlign(meta) === 'right' ? 'text-right tabular-nums' : undefined;
}

interface AppTableRowModels {
  expanded?: boolean;
  filtered?: boolean;
  paginated?: boolean;
  sorted?: boolean;
}

/**
 * The optional feature APIs shared by ERP tables. Row-model processors remain
 * opt-in so server-driven tables do not accidentally process their current page
 * again on the client.
 */
function createAppTableFeatures({
  expanded = false,
  filtered = false,
  paginated = false,
  sorted = false,
}: AppTableRowModels = {}) {
  return tableFeatures({
    columnFilteringFeature,
    filterFns: {
      arrIncludes: filterFn_arrIncludes,
      equals: filterFn_equals,
      inDateRange: filterFn_inDateRange,
      inNumberRange: filterFn_inNumberRange,
      includesString: filterFn_includesString,
      weakEquals: filterFn_weakEquals,
    },
    columnFacetingFeature,
    globalFilteringFeature,
    columnOrderingFeature,
    columnSizingFeature,
    columnResizingFeature,
    columnVisibilityFeature,
    rowExpandingFeature,
    rowPaginationFeature,
    rowSelectionFeature,
    rowSortingFeature,
    sortFns: {
      alphanumeric: sortFn_alphanumeric,
      datetime: sortFn_datetime,
      text: sortFn_text,
    },
    ...(expanded ? { expandedRowModel: createExpandedRowModel() } : {}),
    ...(filtered ? { filteredRowModel: createFilteredRowModel() } : {}),
    ...(paginated ? { paginatedRowModel: createPaginatedRowModel() } : {}),
    ...(sorted ? { sortedRowModel: createSortedRowModel() } : {}),
  });
}

export type AppTableFeatures = ReturnType<typeof createAppTableFeatures>;

export const appTableFeatures = createAppTableFeatures();
export const expandedAppTableFeatures = createAppTableFeatures({
  expanded: true,
});
export const paginatedAppTableFeatures = createAppTableFeatures({
  paginated: true,
});
export const sortedAppTableFeatures = createAppTableFeatures({ sorted: true });
export const clientAppTableFeatures = createAppTableFeatures({
  filtered: true,
  paginated: true,
  sorted: true,
});

// App-level aliases keep the shared feature type consistent across reusable
// table components and column-definition modules.
export type Cell<
  TData extends RowData,
  TValue extends CellData = CellData,
> = TanStackCell<AppTableFeatures, TData, TValue>;
export type CellContext<
  TData extends RowData,
  TValue extends CellData = CellData,
> = TanStackCellContext<AppTableFeatures, TData, TValue>;
export type Column<
  TData extends RowData,
  TValue extends CellData = CellData,
> = TanStackColumn<AppTableFeatures, TData, TValue>;
export type ColumnDef<
  TData extends RowData,
  TValue extends CellData = CellData,
> = TanStackColumnDef<AppTableFeatures, TData, TValue>;
export type Header<
  TData extends RowData,
  TValue extends CellData = CellData,
> = TanStackHeader<AppTableFeatures, TData, TValue>;
export type ReactTable<TData extends RowData> = TanStackReactTable<
  AppTableFeatures,
  TData
>;
export type Row<TData extends RowData> = TanStackRow<AppTableFeatures, TData>;
export type Table<TData extends RowData> = TanStackTable<
  AppTableFeatures,
  TData
>;
export type TableOptions<TData extends RowData> = TanStackTableOptions<
  AppTableFeatures,
  TData
>;
export type VisibilityState = ColumnVisibilityState;

export {
  flexRender,
  functionalUpdate,
  useTable,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnResizeMode,
  type ColumnSizingState,
  type CellData,
  type ExpandedState,
  type OnChangeFn,
  type PaginationState,
  type RowSelectionState,
  type RowData,
  type SortingState,
  type Updater,
} from '@tanstack/react-table';
